import { switchMap } from 'rxjs/operators';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { Component, OnInit, AfterContentInit, OnDestroy, inject, signal, effect, computed } from '@angular/core';
import { Router } from '@angular/router';
import { OrdersService } from './orders.service';
import { CartService } from '../../cart/cart.service';
import { TopService } from '../../../shared/services/top.service';
import { UserService } from '../../../user/user.service';
import { IOrder } from '../../interfaces/model';
import { SharedService } from '../../../shared/shared.service';
import { SharedSource } from '../../../core/shared-source';
import { PubSubService } from '../../../core/pubsub.service';
import { SessionService } from '../../../services/session.service';
import * as models from '../../interfaces/model';
import { OrderStatusEnum } from '../../enums/orderStatusEnum';
import { CustomCurrencyPipe } from '../../../shared/pipes/currency.pipe';
import { ErrorModalComponent } from '../../../shared/ui-components/error-modal/error-modal.component';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { NgIf, NgFor, NgClass, DatePipe } from '@angular/common';

@Component({
  selector: 'app-orders',
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.scss'],
  standalone: true,
  imports: [NgIf, NgFor, NgClass, PaginationModule, ErrorModalComponent, DatePipe, CustomCurrencyPipe]
})
export class OrdersComponent implements OnInit, AfterContentInit, OnDestroy {
  router = inject(Router);
  ordersService = inject(OrdersService);
  topService = inject(TopService);
  sharedService = inject(SharedService);
  userService = inject(UserService);
  cartService = inject(CartService);
  pubSubService = inject(PubSubService);
  sessionService = inject(SessionService);

  upDateOrderConfirmation = signal<boolean>(false);
  orders = signal<models.OrderList[]>(null);
  showModal = signal<boolean>(false);
  showStatusGuide = signal<boolean>(false);
  editOrderPopUpMessage = signal<string>('');
  cancelOrderPopUpMessage = signal<string>('');
  cancelError = signal<boolean>(false);
  showCancelModal = signal<boolean>(false);
  animateOut = signal<boolean>(false);
  animateIn = signal<boolean>(false);
  user_role = signal<string>('');
  orderLoaded = signal<boolean>(false);
  docEntry = signal<number>(-1);
  showChargeOrder = signal<boolean>(false);
  showChargedOrder = signal<boolean>(false);
  chargingOrder = signal<models.Order | null>(null);
  showEmpty = signal<boolean>(false);
  loadingOrderList = signal<boolean>(false);
  preloaderItemList = Array(9);
  pagination = signal(this.ordersService.orderListPagination);
  paginationQuery = signal<string>(this.ordersService.defaultOrderRequest);

  readonly PAGINATION_TOTAL_PAGES = 5;
  OrderStatusEnum = OrderStatusEnum;
  hasInitializedChangeCustomer = false;

  constructor() {
    this.ordersService.filterAndMapSalesOrderStatus();
    
    effect(() => {
      const changeCustomerSignal = this.pubSubService.getSignal(SharedSource.changeCustomer);
      const value = changeCustomerSignal();
      if (!this.hasInitializedChangeCustomer) {
        this.hasInitializedChangeCustomer = true;
        return;
      }

      if (value) {
        this.loadOrderHistory();
      }

    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.sharedService.isLogoutPage = false;
    this.user_role.set(this.sessionService.userRole);
    const isCustomerSelected = this.sessionService.isCustomerSelected === 'YES';

    if (this.user_role() === 'BUYER' || (['SALES', 'SUPER'].includes(this.user_role()) && isCustomerSelected)) {
      this.loadOrderHistory();
    }
  }

  ngAfterContentInit() {
    this.topService.showSearch = true;
    this.topService.isCatalog = false;
  }

  ngOnDestroy(): void {
    if (this.topService.isMobile) {
      document.documentElement.classList.remove('gg-modal', 'is-modal');
    }
  }

  async loadOrderHistory() {
    this.loadingOrderList.set(true);
    this.topService.loading = true;

    try {
      const orderListResponse = await firstValueFrom(this.ordersService.getOrderList(this.paginationQuery())); //  Observable to Promise using firstValueFrom() de RxJs

      if (orderListResponse) {
        this.pagination.set({
          currentPage: orderListResponse.pagination.currentPage,
          pageSize: orderListResponse.pagination.pageSize,
          totalCount: orderListResponse.pagination.totalCount,
          totalPages: this.PAGINATION_TOTAL_PAGES,
        });

        if (!orderListResponse || orderListResponse?.values.length === 0) {
          this.sharedService.editOrderMode = false;
          this.sharedService.noOrders = true;
        }

        this.orders.set([]);
        this.orderLoaded.set(true);
        if (orderListResponse && orderListResponse?.values.length > 0) {
          this.orders.set([...orderListResponse.values]);
          this.sharedService.noOrders = false;
        }

      }
    } catch (error) {
      this.orders.set([]);
      if (error.status === 400) {
        this.cartService.erroronline = true;
      }
      this.sharedService.handleBuyerHttpError(error, this.ordersService.ordersErrorModal);
    } finally {
      this.loadingOrderList.set(false);
      this.topService.loading = false;

      if (this.showCancelModal()) {
        this.closeCancelConfirm();
      }
    }
  }

  closeCancelConfirm(): void {
    this.animateIn.set(false);
    this.animateOut.set(true);
    setTimeout(() => {
      this.showCancelModal.set(false);
      this.animateOut.set(false);
    }, 400);
    event.stopPropagation();
  }

  pageChanged(event) {
    this.paginationQuery.set(`?PageNumber=${event.page}&PageSize=${event.itemsPerPage}`);
    this.loadOrderHistory();
  }

  gotoShopping(): void {
    this.router.navigate(['buyer/home']);
  }

  closeModal(): void {
    this.animateIn.set(false);
    this.animateOut.set(true);
    setTimeout(() => {
      this.showModal.set(false);
      this.animateOut.set(false);
    }, 400);
  }

  openModal(): void {
    this.showModal.set(true);
    this.animateIn.set(true)
  }

  openStatusGuide() {
    document.documentElement.classList.add('gg-modal', 'is-modal');
    this.showStatusGuide.set(true);
    this.animateIn.set(true);
  }

  closeStatusGuide() {
    this.animateIn.set(false);
    this.animateOut.set(true);
    setTimeout(() => {
      this.showStatusGuide.set(false)
      this.animateOut.set(false)
      document.documentElement.classList.remove('gg-modal', 'is-modal');
    }, 200);
  }

  cancelConfirm(order: models.Order): void {
    this.docEntry.set(order.docEntry);
    this.showCancelModal.set(true);
    this.animateIn.set(true);
    this.animateOut.set(false);
    this.cancelError.set(false);
    event.stopPropagation();
  }

  cancelOrderYes(): void {
    this.ordersService.cancelOrder(this.docEntry()).subscribe(
      () => {
        this.loadOrderHistory();
      },
      (err) => {
        this.loadOrderHistory();
        this.cancelError.set(true);
      }
    );
  }

  popUpTextMessages = computed(() => {
    if (this.showModal()) {
      return 'To edit an order, your shopping cart will be cleared.';
    } else if (this.showCancelModal()) {
      return this.cancelError() ? 'You cannot cancel this order' : 'Are you sure you want to CANCEL this order?';
    }
    return '';
  });

  splitOrder(order: models.Order): void {
    this.topService.loading = true;
    this.ordersService.getOrderDetails(order.docEntry.toString()).subscribe(
      (orderData) => {
        this.topService.loading = false;
        this.ordersService.orderChosen = orderData;
        this.router.navigate(['/user/split', order.docEntry]);
      },
      (err) => {
        this.topService.loading = false;
        this.sharedService.handleBuyerHttpError(err, this.ordersService.ordersErrorModal);
      }
    );
    event.stopPropagation();
  }

  updateOrderConfirmDialog(order: models.Order): void {
    this.ordersService.selected_doc_number = order.docNumber;
    this.ordersService.selected_order_number = order.docEntry;
    this.ordersService.handleFirstOrderLoadRequired = true;
    this.openModal();
    this.sessionService.selectedOrderNumber = this.ordersService.selected_order_number.toString();
    this.sessionService.selectedDocNumber = this.ordersService.selected_doc_number.toString();
    this.upDateOrderConfirmation.set(true);
    event.stopPropagation();
  }

  updateOrder(): void {
    this.closeModal();
    this.topService.loading = true;
    this.ordersService.updateOrder(this.ordersService.selected_order_number).subscribe(
      () => {
        this.sharedService.editOrderMode = true;
        this.router.navigate(['cart'], { queryParams: { updatingOrder: 'Y' } });
      },
      (err) => {
        this.topService.loading = false;
        this.sharedService.handleBuyerHttpError(err, this.ordersService.ordersErrorModal);
      }
    );
  }

  showError(errorModal: models.IBuyerErrorModal): void {
    this.ordersService.ordersErrorModal = errorModal;
  }

  hideError(): void {
    this.ordersService.ordersErrorModal.isShown = false;
  }

  showChargeModal(order: models.Order, event = null) {
    this.chargingOrder.set(order);
    this.showChargeOrder.set(true);
    event.stopPropagation();
  }

  chargeOrder(): void {
    if (this.user_role() === 'SUPER') {
      this.topService.loading = true;
      this.ordersService.chargeOrder(this.chargingOrder().payment.id, this.chargingOrder().orderTotal).subscribe(
        (res) => {
          this.topService.loading = false;
          this.showChargeOrder.set(false);
          if (res.error) {
            const modalData = {
              bodyText: res.error.message,
              isShown: true,
            };
            this.showError(modalData);
            return;
          }
          this.showChargedOrder.set(true);
          const orderIndex = this.orders().indexOf(this.orders().find((e) => e.docEntry === this.chargingOrder().docEntry));
          this.orders[orderIndex].payment.status = 'Applied';
        }
      );
    }
  }

  closeChargedModal() {
    this.chargingOrder.set(null);
    this.showChargedOrder.set(false);
  }

  getStatusProgressColor(order: models.OrderList, index: number): string {
    var result = '';

    if (order.progressStatus > models.SalesOrderStatus.ProcessingStock) {
      index++;
    }

    if (index < order.progressStatus) {
      result = this.ordersService.statusColorMap[order.progressStatus].color;
    } else {
      result = this.ordersService.defaultStatusColor;
    }

    return result;
  }

  openPdf(event: Event, docEntry: number): void {
    if (this.topService.loading) {
      return;
    }
    this.ordersService.openPdf(event, docEntry);
  }
}
